import React from "react";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  Star,
  Phone,
  BadgeCheck,
  Medal,
} from "lucide-react";
import { HERO_BG } from "./Navbar";
import { trackCall, trackQuote } from "@/lib/track";
import { scrollToId } from "@/lib/scrollToId";
import { FormEmbed } from "./FormEmbed";

export const Hero: React.FC = () => {
  return (
    <section className="relative pt-32 pb-0 lg:pt-44 lg:pb-0 overflow-hidden bg-[#080810] min-h-[520px] lg:min-h-[640px]">
      {/* Background image with dark overlay for readability */}
      <div
        className="absolute inset-0 z-0 overflow-hidden pointer-events-none"
        aria-hidden="true"
      >
        <img
          src="https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6ab280c8880214b00ee9151f.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          decoding="async"
        />
        {/* Gradient from the LEFT (behind copy) - dark to fully transparent (0 opacity) on the right */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(8,8,16,0.88)_0%,rgba(8,8,16,0.65)_30%,rgba(8,8,16,0.25)_60%,transparent_100%)]" />
      </div>

      {/* Main Hero Grid */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-16 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column - Copy & Value Props */}
          <div className="lg:col-span-7 text-left pl-1 sm:pl-0 pr-6 sm:pr-12 lg:pr-20 max-w-[68%] sm:max-w-[75%] lg:max-w-none">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.1] mb-5">
              Liquid Screed <br />
              Specialists <br />
              <span className="text-[#C9A84C]">in Exeter & Devon</span>
            </h1>

            <p className="text-base sm:text-lg text-white font-medium mb-10 sm:mb-8 lg:mb-12 max-w-2xl">
              Smooth, level floors for new builds, extensions, renovations and
              underfloor heating across Exeter and Devon.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3 sm:gap-4 mb-8 w-full max-w-xs">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  trackQuote("hero");
                  scrollToId("#contact");
                }}
                className="inline-flex items-center justify-center gap-2 bg-[#C9A84C] hover:bg-[#b89539] text-stone-950 font-bold text-sm sm:text-base px-5 sm:px-6 py-3 rounded-lg transition-all shadow-lg hover:shadow-amber-500/20 group whitespace-nowrap w-full"
              >
                <span>Get a Free Quote</span>
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="tel:077657126609"
                onClick={() => trackCall("07765 712609", "hero")}
                className="inline-flex items-center justify-center gap-2 border border-stone-700 bg-stone-900/80 hover:bg-stone-800 text-white font-bold text-sm sm:text-base px-5 sm:px-6 py-3 rounded-lg transition-all whitespace-nowrap w-full"
              >
                <Phone className="w-4 h-4 text-[#C9A84C]" />
                <span>Call us</span>
              </a>
            </div>
          </div>

          {/* Right Column - Quick Quote Card (desktop only; hidden on mobile for performance) */}
          <div className="hidden lg:block lg:col-span-5 relative">
            <div className="bg-white border border-[#C9A84C]/40 rounded-3xl p-6 sm:p-8 shadow-2xl relative">
              {/* 10-Year Guarantee Circular Badge - top right corner */}
              <div className="absolute -top-5 -right-5 z-20 flex flex-col items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#C9A84C] text-stone-950 shadow-xl border-4 border-[#0A0A0B] text-center p-1 transform rotate-6 hover:rotate-0 transition-transform">
                <span className="text-[10px] sm:text-[11px] font-black leading-none uppercase tracking-tight">
                  10-YEAR
                </span>
                <span className="text-[9px] sm:text-[10px] font-bold leading-tight uppercase">
                  GUARANTEE
                </span>
                <span className="text-[7px] sm:text-[8px] font-medium leading-none text-stone-900 mt-0.5">
                  ON CEMFLOOR
                </span>
              </div>

              <h3 className="text-2xl font-bold text-stone-900 mb-4">
                Get My Fixed Screed Quote
              </h3>

              <div className="space-y-4">
                <FormEmbed instanceId="hero" height={380} defer />
              </div>

              <div className="mt-5 pt-4 border-t border-stone-200 grid grid-cols-3 gap-2 text-[11px] text-stone-500 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3 text-[#C9A84C]" />
                  <span>Same-day reply</span>
                </div>
                <div className="flex items-center justify-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-[#C9A84C]" />
                  <span>Fully insured</span>
                </div>
                <div className="flex items-center justify-center gap-1">
                  <Star className="w-3 h-3 text-[#C9A84C] fill-[#C9A84C]" />
                  <span>5★ reviewed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust bar - solid black band filling space to the next section */}
      <div className="relative z-10 w-full bg-[#080810]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-row items-center justify-center gap-4 sm:gap-8 lg:gap-16 py-4 sm:py-6 lg:py-7">
            <div className="flex flex-col lg:flex-row items-center gap-1.5 lg:gap-2.5 flex-1 sm:flex-none">
              <BadgeCheck className="w-6 h-6 sm:w-8 sm:h-8 text-[#C9A84C] flex-shrink-0" />
              <span className="text-[10px] sm:text-sm font-semibold text-stone-200 text-center leading-tight">
                Cemfloor Approved
              </span>
            </div>
            <div className="w-px h-8 sm:h-10 bg-stone-700/60" />
            <div className="flex flex-col lg:flex-row items-center gap-1.5 lg:gap-2.5 flex-1 sm:flex-none">
              <ShieldCheck className="w-6 h-6 sm:w-8 sm:h-8 text-[#C9A84C] flex-shrink-0" />
              <span className="text-[10px] sm:text-sm font-semibold text-stone-200 text-center leading-tight">
                Fully Insured
              </span>
            </div>
            <div className="w-px h-8 sm:h-10 bg-stone-700/60" />
            <div className="flex flex-col lg:flex-row items-center gap-1.5 lg:gap-2.5 flex-1 sm:flex-none">
              <Medal className="w-6 h-6 sm:w-8 sm:h-8 text-[#C9A84C] flex-shrink-0" />
              <span className="text-[10px] sm:text-sm font-semibold text-stone-200 text-center leading-tight">
                Quality Guaranteed
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
